/**
 * A module representing training.
 * @module training
 * @exports Training
 * @see Training
 * @requires module:helpers
 * @example
 * var Training = require('training');
 */
define('training',['helpers', 'pubsub'], function(helpers, PubSub) {

    var training;

    function getQuesionMaxPoints(answers, questions) {
        var total = 0,
            arr = [];

        questions.forEach(function(question) {
            arr = answers.filter(function(answer) {
                return answer.questionId === question.id;
            });

            total += getMaxPoints(arr);
        });

        return total;
    }

    function getMaxPoints(answers) {
        var max = 0;

        answers.forEach(function(answer) {
            if (answer.points > max) {
                max = answer.points;
            }
        });

        return max;
    }

    function getTotalPoints(answers) {
        var total = 0;

        for (var i = 0; i < answers.length; i++) {
            total += answers[i].points;
        }

        return total;
    }

    function getEntityPoints(entity) {
        var typeID = (entity) ? entity.typeId : 0,
            displayMode = (entity) ? entity.contentEntity.displayMode : 0,
            answers = (entity) ? entity.answers : [],
            questions = (entity) ? entity.questions : [];

        switch(typeID) {
        case 10:                                                                                            // Content page
            return 0;
        case 12:                                                                                            // Open question
            return 0;
        case 13:                                                                                            // Multiple choice
            switch(displayMode) {
            case 3:                                                                                             // Checkboxes
                return getTotalPoints(answers);
            default:                                                                                            // Radio & Push buttons
                return getMaxPoints(answers);
            }
        case 14:                                                                                            // Yes No
        case 15:                                                                                            // Set in order
        case 16:                                                                                            // Martch pair
            return questions.length;
        case 17:                                                                                            // Evaluate
            return getMaxPoints(answers) * questions.length;
        case 18:                                                                                            // Fill in blanks
            return getQuesionMaxPoints(answers, questions);
        case 20:                                                                                            // Sort to groups
            return answers.length;
        default:
            return 0;
        }
    }

    function getFlattenEntities(entities) {
        var res = {},
            flatten = function(arr) {
                for (var i = 0; i < arr.length; i++) {
                    res[arr[i].contentEntityId] = arr[i];

                    if (arr[i].children && arr[i].children.length) {
                        flatten(arr[i].children);
                    }
                }
            }

        flatten(entities);

        return res;
    }

    /**
     * @constructor
     * @alias Training
     * @param {Object}  options
     * @param {Array}   [options.contentEntities = []] - Content entities
     * @param {String}  [options.dateModified = ''] - Date modified
     * @param {Array}   [options.componentsJSON = []] - Training componentsJSON
     * @param {Object}  [options.content = {}] - Flatten formatted content entities
     * @param {String}  [options.containerID = ''] - Container ID
     * @param {Object}  [options.entities = {}] - Flatten content entities
     * @param {String}  [options.entityID = ''] - current Entity ID
     * @param {String}  [options.entityURL = ''] - Entity URL
     * @param {Object}  [options.images = {}] - Images
     * @param {Integer} [options.imagePosition = 0] - Default image position
     * @param {Integer} [options.imageScaleMode = 0] - Default image scale mode
     * @param {String}  [options.lastQuizPageID = ''] - Last training Quiz page ID
     * @param {String}  [options.mediaURL = ''] - Media URL
     * @param {Object}  [options.modularMenuPage = {}] - Modular menu properties
     * @param {String}  [options.modularMenuPageID = ''] - Modular menu page id
     * @param {Boolean} [options.offlinePackage = false] - Is training an offline package
     * @param {Boolean} [options.previewMode = false] - Is training in preview mode
     * @param {Integer} [options.score = 0] - training score
     * @param {Boolean} [options.scormPackage = false] - Is training a scorm package
     * @param {String}  [options.sequence = ''] - Is training sequence required
     * @param {Boolean} [options.showQuizFeedback = false] - show quiz feedback
     * @param {String}  [options.state = ''] - training state
     * @param {Integer} [options.status = 0] - training status
     * @param {Boolean} [options.svgsInIndexHtml = false] - Is svgs in index html
     * @param {String}  [options.tokenID = ''] - Token ID
     * @param {Array}   [options.trainingMap = []] - Training map
     */
    function Training(options) {
        options = options || {};

        this.contentEntities = Array.isArray(options.contentEntities) ? options.contentEntities : [];

        this.dateModified = (options.dateModified && typeof options.dateModified === 'string') ? options.dateModified : '';

        this.componentsJSON = [];

        this.content = {};

        this.containerID = '';

        this.entities = {};

        this.entityID = (options.entityID && typeof options.entityID === 'string') ? options.entityID : '';

        this.entityURL = (options.entityURL && typeof options.entityURL === 'string') ? options.entityURL : '';

        this.images = (options.images !== null && typeof options.images === 'object') ? options.images : {};

        this.imagePosition = 0;

        this.imageScaleMode = 0;

        this.lastQuizPageID = '';

        this.mediaURL = (options.mediaURL && typeof options.mediaURL === 'string') ? options.mediaURL : '';

        this.modularMenuPage = {};

        this.modularMenuPageID = '';

        this.offlinePackage = !!options.offlinePackage;

        this.previewMode = !!options.previewMode;

        this.score = -1;

        this.scormPackage = !!options.scormPackage;

        this.sequence = false;

        this.showQuizFeedback = false;

        this.state = options.state || '';

        this.status = -1;

        this.svgsInIndexHtml = false;

        this.tokenID = options.tokenID || '';

        this.trainingMap = Array.isArray(options.trainingMap) ? options.trainingMap : [];
    }

    /**
     * Sets current training entity ID.
     * @param {String} state - training state
     */
    Training.prototype.setEntityID = function(ID) {
        if (!ID) {
            return;
        }

        this.entityID = ID;
    };

    /**
     * Sets training state.
     * @param {String} state - training state
     */
    Training.prototype.setState = function(state) {
        this.state = state;
    };

    /**
     * Sets training score.
     * @param {Integer} score - training score
     */
    Training.prototype.setScore = function(score) {
        this.score = score;
    };

    /**
     * Sets training status.
     * @param {Integer} status - training status
     */
    Training.prototype.setStatus = function(status) {
        this.status = status;
    };

    /**
     * Updates training entity content, used in livereload.
     * @param {Object} content - entity
     */
    Training.prototype.setEntity = function(content) {
        var ID = content.contentEntityId,
            setEntity = function(ID, arr) {
                for (var i = 0; i < arr.length; i++) {
                    if (arr[i].contentEntityId === ID) {
                        arr[i] = content;
                    }
                    if (arr[i].children.length) {
                        setEntity(ID, arr[i].children);
                    }
                }
            };

        if (this.entities[ID]) {
            this.entities[ID] = content;
        }

        setEntity(ID, this.contentEntities);
    };

    /**
     * Returns entity by ID.
     * @param {String} ID - content entity ID
     * @returns {Object}
     */
    Training.prototype.getEntity = function(ID) {
        return this.entities[ID] || null;
    };

    /**
     * Returns formatted entity content by ID or by entity object.
     * @param {} prop - Entity ID or entity object
     * @returns {Object}
     */
    Training.prototype.getContent = function(prop) {
        var ID = '',
            obj = (typeof prop === 'string') ? this.getEntity(prop) : (prop && prop.hasOwnProperty('contentEntity')) ? prop : null,
            entity,
            content;

        if (!obj) {
            return {};
        }

        ID = obj.contentEntityId;

        if (this.content[ID]) {
            return this.content[ID];
        }

        entity = helpers.misc.cloneObject(obj);
        content = entity.contentEntity || {};

        entity.ID = ID;

        entity.canSubmitMultipleTimes = !!content.canSubmitMultipleTimes;

        entity.componentsJSON = this.parseComponents(content.componentsJson);

        entity.content = content.content;

        entity.customCSS = content.customCss || '',
        entity.customHTML = content.customUiTemplates || '',
        entity.customJS = content.customJavascript || '',

        entity.dateModified = content.dateModified;
        entity.displayMode = content.displayMode;

        entity.correctFeedbackID = content.correctAnswerFeedbackId;
        entity.incorrectFeedbackID = content.incorrectAnswerFeedbackId;

        entity.hasCorrectAnswers = !!content.hasCorrectAnswers;

        entity.parentID = content.parentId;

        entity.passAlways = !!content.passAlways;
        entity.passThreshold = content.passThreshold;
        entity.passedFeedback = content.passedFeedback;
        entity.failedFeedback = content.failedFeedback;
        entity.enableMovingBackwards = content.enableMovingBackwards;
        entity.completeDespiteResults = content.completeDespiteResults;
        entity.requiresCompletion = content.requiresCompletion;

        entity.imageID = content.imageId;
        entity.imageURL = this.getMediaURL(entity.imageID);
        entity.imageContentType = this.getMediaContentType(entity.imageID);

        entity.naviTargetID = content.naviTargetId;
        entity.text = content.text;
        entity.typeID = entity.typeId;

        entity.shuffleTasks = !!content.shuffleTasks;
        entity.skipFeedback = !!content.skipFeedback;

        entity.requiresAnswer = !!content.requiresAnswer;
        entity.requiresCompletion = !!content.requiresCompletion;

        this.content[ID] = entity;

        return entity;
    };

    /**
     * Returns training Component content. If passed entity ID, checks if it contains too.
     * @param {} kind - Component kind
     * @param {} ID - Entity ID
     * @returns {Object}
     */
    Training.prototype.getComponent = function(str, ID) {
        str = str || '';

        var tComponent = helpers.component.getContent(this.componentsJSON, str),
            content = (ID) ? this.getContent(ID) : null,
            eComponent = (content) ? helpers.component.getContent(content.componentsJSON, str) : {};

        return (Object.keys(eComponent).length) ? eComponent : tComponent;
    };

    /**
     * Returns last Quiz page ID in training map.
     * @param {Array} [trainingMap = Training.trainingMap] - Training map
     * @returns {String}
     */
    Training.prototype.getLastQuizPageID = function(trainingMap) {
        trainingMap = trainingMap || this.trainingMap;

        var i = trainingMap.length,
            entity,
            parent;

        while (i--) {
            entity = this.getEntity(trainingMap[i]);

            if (!helpers.entity.isQuizPage(entity)) {
                continue;
            }

            parent = this.getEntity(entity.contentEntity.parentId);

            if (helpers.entity.isQuizGamePage(parent)) {
                continue;
            }

            return trainingMap[i];
        }

        return '';
    };

    /**
     * Set training properties
     * @param {Object}  prop
     * @param {Array}   [prop.contentEntities] - Content entities
     * @param {String}  [prop.componentsJSON] - Training stingified components JSON
     * @param {Boolean} [prop.enforceSequence] - Enforce training sequence
     * @param {Object}  [prop.images] - Training image information
     * @param {String}  [prop.containerID] - Training container ID
     * @param {String}  [prop.dateModified] - Training modified date
     */
    Training.prototype.setProperties = function(prop) {

        if (prop.contentEntities) {
            this.setContentEntities(prop.contentEntities);
            this.setModularMenu(prop.contentEntities);
        }

        if (prop.componentsJSON) {
            this.componentsJSON = JSON.parse(prop.componentsJSON) || [];
        }

        this.sequence = !!prop.enforceSequence;

        this.imagePosition = prop.imagePosition;

        this.imageScaleMode = prop.imageScaleMode;

        this.svgsInIndexHtml = !!prop.svgsInIndexHtml;

        if (prop.images && typeof prop.images === 'object') {
            this.images = prop.images;
        }

        this.containerID = prop.containerID || '';

        if (prop.dateModified && typeof prop.dateModified === 'string') {
            this.dateModified = prop.dateModified;
        }
    };

    /**
     * Sets training modular menu properties
     * @param {Array}   [entities] - Content entities
     */
    Training.prototype.setModularMenu = function(entities) {
        var content,
            componentsJSON;

        if (!Array.isArray(entities)) {
            return;
        }

        for (var i = 0; i < entities.length; i++) {
            componentsJSON = (entities[i].contentEntity && entities[i].contentEntity.componentsJson) ? JSON.parse(entities[i].contentEntity.componentsJson) : null;

            if (!componentsJSON) {
                continue;
            }

            content = helpers.component.getContent(componentsJSON, 'ModularNavigation');

            if (!Object.keys(content).length) {
                continue;
            }

            this.modularMenuPage.ID = entities[i].contentEntityId;

            if (content.returnToMainFromSections) {
                this.modularMenuPageID = entities[i].contentEntityId;
            }

            this.modularMenuPage.colCount = content.colCount;
            this.modularMenuPage.customBodyClass = content.customBodyClass;
            this.modularMenuPage.hideToc = content.hideToc;
            this.modularMenuPage.items = helpers.component.parseProp(content, 'items', 'ModularMenuItem');
            this.modularMenuPage.returnToMainFromSections = content.returnToMainFromSections;
            this.modularMenuPage.rowCount = content.rowCount;
            this.modularMenuPage.showNumbers = content.showNumbers;

            return;
        }
    }

    /**
     * Sets training content entities, training map, flatten content entities & last quiz page id.
     * @param {Array} entities - Training content entities
     */
    Training.prototype.setContentEntities = function(entities) {
        if (!Array.isArray(entities)) {
            return;
        }

        this.contentEntities = entities;

        this.trainingMap = helpers.content.getTrainingMap(entities);

        this.entities = getFlattenEntities(entities);

        this.lastQuizPageID = this.getLastQuizPageID(this.trainingMap);
    };

    /**
     * Appends images paramater to URL by image ID
     * @param {String} imageID - image ID
     */
    Training.prototype.appendParams = function(imageID) {
        var modified = (this.images && this.images.hasOwnProperty(imageID)) ? this.images[imageID].dateModified : '';

        if (this.offlinePackage) {
            return '?v=' + helpers.URL.parseModified(this.dateModified) + '&d=' + helpers.URL.parseModified(modified);
        } else {
            return '/' + helpers.URL.parseModified(this.dateModified) + '/' + helpers.URL.parseModified(modified);
        }
    };

    /**
     * Returns full media path by image ID.
     * @param {String} imageID - image ID
     * @returns {String}
     */
    Training.prototype.getMediaURL = function(imageID) {
        var res = '';

        if (!imageID) {
            return res;
        }

        res = this.mediaURL + encodeURI(imageID);

        res += this.appendParams(imageID);

        return res;
    };

    /**
     * Returns full media path by image URL.
     * @param {String} imageURL - image URL
     * @returns {String}
     */
    Training.prototype.parseMediaURL = function(imageURL) {
        if (!imageURL) {
            return '';
        }

        return (imageURL.indexOf('urn:gimlet:') === 0) ? this.getMediaURL(imageURL.substr(11)) : imageURL;
    }

    /**
     * Returns media content type by image ID.
     * @param {String} imageID - image ID
     * @returns {String}
     */
    Training.prototype.getMediaContentType = function(imageID) {
        return (this.images && this.images.hasOwnProperty(imageID)) ? this.images[imageID].contentType : '';
    };

    /**
     * Returns media content type by image URL.
     * @param {String} imageURL - image URL
     * @returns {String}
     */
    Training.prototype.parseMediaContentType = function(imageURL) {
        if (!imageURL) {
            return '';
        }

        return (imageURL.indexOf('urn:gimlet:') === 0) ? this.getMediaContentType(imageURL.substr(11)) : '';
    };

    /**
     * Returns parsed components JSON.
     * @param {String} componentsJson - Stringified JSON
     * @returns {Array}
     */
    Training.prototype.parseComponents = function(str) {
        if (!str) {
            return [];
        }

        var i, arr,
            a = str.match(/<a[^>]+\slinktypeid=\\"1\\"[^>]*>/gmi),
            img = str.match(/<img[^>]+\slinktypeid=\\"1\\"[^>]*>/gmi);

        if (!a && !img) {
            return JSON.parse(str);
        }

        if (img) {
            for (i = 0; i < img.length; i++) {
                arr = img[i].match(/linkentityid=\\"([^\s]*)\\"\s?/i);

                if (arr) {
                    str = str.replace(img[i], img[i].replace(/src=\\"([^\s]*)\\"\s/i, 'src=\\"' + training.getMediaURL(arr[1]) + '\\" '));
                }
            }
        }

        if (a) {
            for (i = 0; i < a.length; i++) {
                arr = a[i].match(/linkentityid=\\"([^\s]*)\\"\s?/i);

                if (arr) {
                    str = str.replace(a[i], a[i].replace(/href=\\"([^\s]*)\\"\s?/i, 'href=\\"' + training.getMediaURL(arr[1]) + '\\" '));
                }
            }
        }

        return JSON.parse(str);
    }

    /**
     * Returns Entity points by entity ID.
     * @param {String} ID - Entity ID
     * @param {Boolean} [inScore=true] - Enable Entity check if is it in score
     * @returns {Integer}
     */
    Training.prototype.getEntityPoints = function(ID, inScore) {
        var entity = this.getEntity(ID);

        inScore = (inScore === false) ? false : true;

        if (!entity) {
            return 0;
        }

        if (helpers.entity.isContentPage(entity)) {
            return 0;
        }

        if (inScore && !helpers.entity.isTaskInScore(entity)) {
            return 0;
        }

        return getEntityPoints(entity);
    };

    /**
     * Returns Quiz page points by Quiz page ID.
     * @param {String} ID - Quiz page ID
     * @param {Boolean} [inScore=true] - Enable Entity check if is it in score
     * @returns {Integer}
     */
    Training.prototype.getQuizPagePoints = function(ID, inScore) {

        var entity = this.getEntity(ID),
            arr = (entity && entity.children) ? entity.children : [],
            total = 0;

        inScore = (inScore === false) ? false : true;

        if (!entity || !helpers.entity.isQuizPage(entity)) {
            return 0;
        }

        for(var i = 0; i < arr.length; i++) {
            total += this.getEntityPoints(arr[i].contentEntityId, inScore);
        }

        return total;
    };

    /**
     * Returns Task answers text (returns correct if answerID is empty).
     * @param {String} ID - Page ID
     * @param {Array} [answersID = []] - Page answers ID
     * @returns {Array}
     */
    Training.prototype.getTaskAnswers = function(ID, answersID) {
        answersID = answersID || [];

        var res = [],
            entity = this.getEntity(ID),
            answers;

        if (!entity || !helpers.entity.isTaskPage(entity)) {
            return res;
        }

        answers = entity.answers;

        for(var i = 0; i < answers.length; i++) {
            if (answersID.length) {
                if (answersID.indexOf(answers[i].id) !== -1) {
                    res.push(answers[i].text);
                }
            } else if (answers[i].points > 0) {
                res.push(answers[i].text);
            }
        }

        return res;
    };

    return function(options) {

        if (!training) {
            training = new Training(options);

            PubSub.subscribe('page-load-init', function(prop) {
                var entityID = (prop.entity && prop.entity.ID) ? prop.entity.ID : '';

                if (entityID) {
                    training.setEntityID(entityID);
                }
            });

            PubSub.subscribe('page-load-complete', function() {
                training.setState('');
            });
        }

        return training;
    };

});

